import { useState, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/base/SeoHead';
import { useTheme } from '@/context/ThemeContext';
import { submitContactForm } from '@/lib/api/forms';
import { useBadgeVisibility } from '@/context/BadgeVisibilityContext';
import { useSiteContent } from '@/context/SiteContentContext';

type SubmitStatus = 'idle' | 'loading' | 'success' | 'error';

export default function ContactPage() {
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [charCount, setCharCount] = useState(0);
  const { theme } = useTheme();
  const { settings } = useBadgeVisibility();
  const { content } = useSiteContent();
  const light = theme === 'light';
  const supportEmail = content.footer.email;
  const contactSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: 'Stingray',
      legalName: 'Cool Tech Marine Air Conditioning LLC',
      url: 'https://stingray-marine.com',
      telephone: '+1-307-381-5172',
      email: supportEmail,
      address: {
        '@type': 'PostalAddress',
        streetAddress: '30 N Gould St Ste R',
        addressLocality: 'Sheridan',
        addressRegion: 'WY',
        postalCode: '82801',
        addressCountry: 'US',
      },
      geo: { '@type': 'GeoCoordinates', latitude: 44.7972, longitude: -106.9563 },
      openingHoursSpecification: [
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '08:00', closes: '17:00' },
      ],
      areaServed: 'Worldwide',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        { '@type': 'Question', name: 'How do I become a Stingray dealer?', acceptedAnswer: { '@type': 'Answer', text: `Apply via our Become a Dealer page or email us at ${supportEmail}.` } },
        { '@type': 'Question', name: 'What is the warranty on Stingray batteries?', acceptedAnswer: { '@type': 'Answer', text: 'Stingray LiFePO4 lithium batteries come with a 5-year warranty. AGM series carries a 3-year warranty. Gel series carries a 2-year warranty.' } },
        { '@type': 'Question', name: 'Does Stingray ship internationally?', acceptedAnswer: { '@type': 'Answer', text: 'Yes. Stingray batteries are distributed in 50+ countries worldwide through our authorized dealer network.' } },
      ],
    },
  ];

  const bg = light ? 'bg-gray-50' : 'bg-[#0D1B2A]';
  const headingColor = light ? 'text-gray-900' : 'text-white';
  const bodyColor = light ? 'text-gray-600' : 'text-white/60';
  const cardBg = light ? 'bg-white border border-gray-200' : 'bg-[#1C2B3A]';
  const cardMuted = light ? 'text-gray-500' : 'text-white/50';
  const cardHeading = light ? 'text-gray-800' : 'text-white';
  const breadcrumbColor = light ? 'text-gray-400' : 'text-white/30';
  const breadcrumbActive = light ? 'text-gray-600' : 'text-white/60';
  const inputBg = light ? 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[var(--accent)]' : 'bg-[#0D1B2A] border-white/10 text-white placeholder-white/30 focus:border-[var(--accent)]';
  const labelColor = light ? 'text-gray-500' : 'text-white/60';
  const faqQ = light ? 'text-gray-700' : 'text-white/70';
  const faqA = light ? 'text-gray-400' : 'text-white/40';

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
    if (message.length > 500) return;

    setStatus('loading');
    try {
      await submitContactForm({
        firstName: (form.elements.namedItem('first_name') as HTMLInputElement).value,
        lastName: (form.elements.namedItem('last_name') as HTMLInputElement).value,
        email: (form.elements.namedItem('email') as HTMLInputElement).value,
        phone: (form.elements.namedItem('phone') as HTMLInputElement).value,
        subject: (form.elements.namedItem('subject') as HTMLSelectElement).value,
        message,
        sourceUrl: window.location.href,
      });
      setStatus('success');
      form.reset();
      setCharCount(0);
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className={`min-h-screen ${bg} transition-colors duration-300`}>
      <SeoHead
        title="Contact Stingray | Dealer Inquiries & Support"
        description="Contact Stingray — product inquiries, dealer applications, technical support and warranty claims. Headquartered in Sheridan, WY. Global distribution in 50+ countries."
        keywords="contact Stingray marine batteries, marine battery dealer inquiry, marine battery support, Cool Tech Marine contact"
        canonical="https://stingray-marine.com/contact"
        schema={contactSchema}
      />
      <Navbar />

      {/* Header */}
      <header className="pt-32 pb-16 w-full px-6 md:px-10">
        <nav aria-label="Breadcrumb" className={`flex items-center gap-2 text-xs ${breadcrumbColor} mb-4`}>
          <a href="/" className="hover:text-[var(--accent)] transition-colors">Home</a>
          <i className="ri-arrow-right-s-line"></i>
          <span className={breadcrumbActive}>Contact</span>
        </nav>
        {settings.contactBadge && (
          <span className="inline-block text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-4" style={{ background: 'color-mix(in srgb, var(--accent) 20%, transparent)', border: '1px solid color-mix(in srgb, var(--accent) 40%, transparent)', color: 'var(--accent)' }}>
            Get in Touch
          </span>
        )}
        <h1 className={`text-4xl md:text-5xl font-black ${headingColor} mb-4`}>Contact Us</h1>
        <p className={`${bodyColor} text-lg max-w-xl`}>
          Have a question about our products, interested in becoming a dealer, or need technical support? We&apos;re here to help.
        </p>
      </header>

      {/* Content */}
      <main className="w-full px-6 md:px-10 pb-20">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Contact Info */}
          <div className="lg:w-80 shrink-0">
            <div className="space-y-6">
              {[
                { icon: 'ri-map-pin-line', title: 'Address', content: <address className={`${cardMuted} text-sm leading-relaxed not-italic`}>30 N Gould St Ste R<br />Sheridan, WY 82801<br />United States</address> },
                { icon: 'ri-phone-line', title: 'Phone', content: <a href="tel:+13073815172" className={`${cardMuted} text-sm hover:text-[var(--accent)] transition-colors cursor-pointer`}>+1 307 381 5172</a> },
                { icon: 'ri-mail-line', title: 'Email', content: <a href={`mailto:${supportEmail}`} className={`${cardMuted} text-sm hover:text-[var(--accent)] transition-colors cursor-pointer`}>{supportEmail}</a> },
                { icon: 'ri-time-line', title: 'Business Hours', content: <p className={`${cardMuted} text-sm leading-relaxed`}>Mon – Fri: 8:00 AM – 5:00 PM MT<br />Sat – Sun: Closed</p> },
              ].map((item, i) => (
                <div key={i} className={`${cardBg} rounded-lg p-6`}>
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg mb-4" style={{ background: 'color-mix(in srgb, var(--accent) 20%, transparent)' }}>
                    <i className={`${item.icon} text-xl`} style={{ color: 'var(--accent)' }}></i>
                  </div>
                  <h3 className={`${cardHeading} font-bold text-sm mb-2`}>{item.title}</h3>
                  {item.content}
                </div>
              ))}

              <div className={`${cardBg} rounded-lg p-6`}>
                <div className="flex items-center justify-between mb-3">
                  <h4 className={`${cardHeading} font-bold text-sm`}>Frequently Asked</h4>
                  <a
                    href="/faq"
                    className="text-xs font-semibold flex items-center gap-1 transition-colors whitespace-nowrap"
                    style={{ color: 'var(--accent)' }}
                  >
                    View all <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
                <div className="space-y-3">
                  {[
                    { q: 'How to become a dealer?', a: 'Apply via the Become a Dealer page or email us.' },
                    { q: 'Do you ship internationally?', a: 'Yes — 50+ countries via our dealer network.' },
                    { q: 'LiFePO4 warranty?', a: '5 years on all LiFePO4 lithium series.' },
                  ].map((faq, i) => (
                    <div key={i}>
                      <p className={`${faqQ} text-xs font-semibold`}>{faq.q}</p>
                      <p className={`${faqA} text-xs mt-0.5`}>{faq.a}</p>
                    </div>
                  ))}
                </div>
                <a
                  href="/faq"
                  className={`mt-4 flex items-center justify-center gap-2 w-full py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer border ${
                    light ? 'border-gray-200 text-gray-500 hover:text-gray-800 hover:border-gray-300' : 'border-white/10 text-white/40 hover:text-white/70 hover:border-white/20'
                  }`}
                >
                  See all FAQs <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="flex-1">
            <div className={`${cardBg} rounded-lg p-8`}>
              <h2 className={`text-2xl font-black ${headingColor} mb-6`}>Send Us a Message</h2>

              {status === 'success' ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-500/20 mx-auto mb-4">
                    <i className="ri-check-line text-green-400 text-3xl"></i>
                  </div>
                  <h3 className={`${headingColor} font-bold text-xl mb-2`}>Message Sent!</h3>
                  <p className={`${bodyColor} text-sm mb-6`}>We&apos;ll get back to you within 1-2 business days.</p>
                  <button
                    onClick={() => setStatus('idle')}
                    className="text-white font-bold px-6 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap text-sm"
                    style={{ background: 'var(--accent)' }}
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form data-readdy-form id="contact-form" onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className={`block ${labelColor} text-xs font-semibold uppercase tracking-wide mb-2`}>First Name *</label>
                      <input type="text" name="first_name" required placeholder="John"
                        className={`w-full border text-sm px-4 py-3 rounded-md focus:outline-none transition-colors ${inputBg}`} />
                    </div>
                    <div>
                      <label className={`block ${labelColor} text-xs font-semibold uppercase tracking-wide mb-2`}>Last Name *</label>
                      <input type="text" name="last_name" required placeholder="Smith"
                        className={`w-full border text-sm px-4 py-3 rounded-md focus:outline-none transition-colors ${inputBg}`} />
                    </div>
                  </div>

                  <div>
                    <label className={`block ${labelColor} text-xs font-semibold uppercase tracking-wide mb-2`}>Email *</label>
                    <input type="email" name="email" required placeholder="john@example.com"
                      className={`w-full border text-sm px-4 py-3 rounded-md focus:outline-none transition-colors ${inputBg}`} />
                  </div>

                  <div>
                    <label className={`block ${labelColor} text-xs font-semibold uppercase tracking-wide mb-2`}>Phone</label>
                    <input type="tel" name="phone" placeholder="+1 (555) 000-0000"
                      className={`w-full border text-sm px-4 py-3 rounded-md focus:outline-none transition-colors ${inputBg}`} />
                  </div>

                  <div>
                    <label className={`block ${labelColor} text-xs font-semibold uppercase tracking-wide mb-2`}>Subject *</label>
                    <select name="subject" required
                      className={`w-full border text-sm px-4 py-3 rounded-md focus:outline-none transition-colors cursor-pointer ${inputBg}`}>
                      <option value="">Select a subject</option>
                      <option value="Product Inquiry">Product Inquiry</option>
                      <option value="Dealer Application">Dealer Application</option>
                      <option value="Technical Support">Technical Support</option>
                      <option value="Warranty Claim">Warranty Claim</option>
                      <option value="General Question">General Question</option>
                    </select>
                  </div>

                  <div>
                    <label className={`block ${labelColor} text-xs font-semibold uppercase tracking-wide mb-2`}>
                      Message * <span className={`${faqA} normal-case font-normal`}>({charCount}/500)</span>
                    </label>
                    <textarea name="message" required maxLength={500} rows={5}
                      placeholder="Tell us how we can help..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className={`w-full border text-sm px-4 py-3 rounded-md focus:outline-none transition-colors resize-none ${inputBg}`} />
                    {charCount > 500 && (
                      <p className="text-red-400 text-xs mt-1">Message exceeds 500 character limit.</p>
                    )}
                  </div>

                  {status === 'error' && (
                    <p className="text-red-400 text-sm">Something went wrong. Please try again.</p>
                  )}

                  <button type="submit" disabled={status === 'loading' || charCount > 500}
                    className="w-full text-white font-bold py-3.5 rounded-md transition-colors cursor-pointer whitespace-nowrap text-sm uppercase tracking-wide disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{ background: 'var(--accent)' }}>
                    {status === 'loading' ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
